import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { PlaceholderHelpPopup } from './PlaceholderHelpPopup';
import { Type, Plus, ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';

interface PlaceholderItem {
  key: string;
  description: string;
  category: string;
}

interface DesignerLeftSidebarProps {
  availablePlaceholders: PlaceholderItem[];
  newStaticText: string;
  setNewStaticText: (value: string) => void;
  addStaticText: (text: string) => void;
  onDragStart: (key: string) => void;
}

export function DesignerLeftSidebar({
  availablePlaceholders,
  newStaticText,
  setNewStaticText,
  addStaticText,
  onDragStart,
}: DesignerLeftSidebarProps) {
  const [openCategories, setOpenCategories] = useState<Set<string>>(new Set(['Offerte', 'Bedrijf']));

  const groupedPlaceholders = availablePlaceholders.reduce((acc, p) => {
    const categoryIndex = acc.findIndex(c => c.category === p.category);
    if (categoryIndex === -1) {
      acc.push({ category: p.category, items: [p] });
    } else {
      acc[categoryIndex].items.push(p);
    }
    return acc;
  }, [] as Array<{ category: string; items: PlaceholderItem[] }>);

  const toggleCategory = (category: string) => {
    setOpenCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const staticTextLabels = ['Project:', 'Datum:', 'Geldig tot:', 'Offerte:', 'Referentie:', 'Aan:', 'KvK:', 'BTW:'];

  return (
    <Card className="w-56 min-w-[180px] border-l flex-shrink-0 flex flex-col h-full overflow-hidden">
      <div className="px-2 py-1.5 border-b bg-muted/30">
        <h3 className="font-semibold text-xs">Velden</h3>
        <p className="text-[9px] text-muted-foreground">Sleep naar PDF</p>
      </div>
      
      <ScrollArea className="flex-1">
        {/* Static Text Section */}
        <div className="p-1.5 border-b bg-orange-500/5">
          <h4 className="text-[9px] font-medium text-orange-700 dark:text-orange-400 mb-1.5 flex items-center gap-1">
            <Type className="h-2.5 w-2.5" />
            Vaste Tekst
          </h4>
          <div className="flex gap-1">
            <Input
              value={newStaticText}
              onChange={(e) => setNewStaticText(e.target.value)}
              placeholder="Typ tekst..."
              className="flex-1 h-6 text-xs"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newStaticText.trim()) {
                  addStaticText(newStaticText.trim());
                  setNewStaticText('');
                }
              }}
            />
            <Button
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => {
                if (newStaticText.trim()) {
                  addStaticText(newStaticText.trim());
                  setNewStaticText('');
                }
              }}
              disabled={!newStaticText.trim()}
            >
              <Plus className="h-3 w-3" />
            </Button>
          </div>
          
          {/* Quick examples */}
          <div className="flex flex-wrap gap-0.5 mt-1.5">
            {staticTextLabels.map(label => (
              <Button
                key={label}
                variant="outline"
                size="sm"
                className="h-4 text-[8px] px-1 border-orange-300 hover:bg-orange-100 dark:border-orange-700 dark:hover:bg-orange-900/30"
                onClick={() => addStaticText(label)}
              >
                {label}
              </Button>
            ))}
          </div>
        </div>
        
        {/* Dynamic Placeholders */}
        <div className="p-1.5 space-y-1">
          {groupedPlaceholders.map(group => (
            <Collapsible
              key={group.category}
              open={openCategories.has(group.category)}
              onOpenChange={() => toggleCategory(group.category)}
            >
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="w-full justify-between h-6 px-1.5 font-medium text-[10px]">
                  <span>{group.category}</span>
                  {openCategories.has(group.category) ? (
                    <ChevronUp className="h-2.5 w-2.5" />
                  ) : (
                    <ChevronDown className="h-2.5 w-2.5" />
                  )}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-0.5 space-y-0.5">
                {group.items.map((p) => (
                  <PlaceholderHelpPopup key={p.key} placeholderKey={p.key}>
                    <div
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/plain', p.key);
                        onDragStart(p.key);
                      }}
                      className="p-1 bg-muted rounded cursor-move hover:bg-accent text-xs transition-colors group"
                    >
                      <div className="flex items-start justify-between gap-1">
                        <div className="min-w-0 flex-1">
                          <code className="text-[9px] font-mono break-all">{p.key}</code>
                          <p className="text-[8px] text-muted-foreground truncate">{p.description}</p>
                        </div>
                        <HelpCircle className="h-2.5 w-2.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                      </div>
                    </div>
                  </PlaceholderHelpPopup>
                ))}
              </CollapsibleContent>
            </Collapsible>
          ))}
        </div>
      </ScrollArea>
    </Card>
  );
}
